/**
 *
 * @param data {Array<{from: string, to: string, value: number}>}
 * @returns {{keys: unknown[], matrix: any[][]}}
 */
export const transformDataForChord = (data) => {
  // Combine ETFs and stock tickers into a single set of unique identifiers
  const identifiers = new Set(data.flatMap(d => [d.from, d.to]));

  // Create a map to index each identifier
  const indexMap = new Map(Array.from(identifiers).map((id, i) => [id, i]));

  // Create a matrix filled with zeros
  let matrix = Array.from({ length: indexMap.size }, () =>
    new Array(indexMap.size).fill(0)
  );

  // Populate the matrix with values
  data.forEach(({ from, to, value }) => {
    const fromIndex = indexMap.get(from);
    const toIndex = indexMap.get(to);
    if (fromIndex !== undefined && toIndex !== undefined) {
      // Assuming the direction is from stock to ETF
      matrix[toIndex][fromIndex] = value; // Invert the indices if needed
    }
  });

  // Create an array of keys ordered by their index
  const keys = Array.from(indexMap.keys());
  return { matrix, keys };
};


/**
 *
 * @param parents {Object<string, {holdings: Array<{symbol: string, wgt: number}>}>}
 * @param maxLimit {number}
 * @returns {*[]}
 */
export const extractEtfHoldingsLinks = (parents, maxLimit)=>{
    let pairs = [];
    for (let k in parents){
        let holdings = [...parents[k].holdings];
        if (holdings.length === 0) continue;
        holdings.sort((a,b)=>b?.wgt??0 - a?.wgt??0);
        if (maxLimit) {
          if (holdings.length > maxLimit) {
            holdings = holdings.slice(0, 50);
          }
        }
        holdings.forEach(h=>{
            pairs.push({from: h.symbol, to: k, value: h.wgt * 100})

        })
    }
    return pairs;
}


/**
 * @param {Array<{from: string, to: string, value: number}>} data
 * @returns {Array<Array<number>>}
 */
export const computeOverlapMatrix = (data) => {
  // Create a set of unique ETFs and stock tickers
  const etfs = new Set(data.map(d => d.to));
  const tickers = new Set(data.map(d => d.from));

  // Create a matrix filled with zeros
  let matrix = Array.from({ length: etfs.size }, () => new Array(etfs.size).fill(0));

  // Create an object to store the minimum weight for each stock in each ETF
  const minWeights = {};

  // Iterate over the data to populate the minWeights object
  data.forEach(({ from, to, value }) => {
    if (!minWeights[from]) {
      minWeights[from] = {};
    }
    if (!minWeights[from][to] || value < minWeights[from][to]) {
      minWeights[from][to] = value;
    }
  });

  // Iterate over the ETFs and compute the overlap percentages
  for (let i = 0; i < etfs.size; i++) {
    for (let j = i; j < etfs.size; j++) {
      const etf1 = Array.from(etfs)[i];
      const etf2 = Array.from(etfs)[j];

      let overlap = 0;

      // Iterate over the tickers and compute the overlap
      for (const ticker of tickers) {
        const weight1 = minWeights[ticker]?.[etf1] || 0;
        const weight2 = minWeights[ticker]?.[etf2] || 0;
        overlap += Math.min(weight1, weight2);
      }

      // Store the overlap percentage in the matrix
      matrix[i][j] = overlap;
      matrix[j][i] = overlap;
    }
  }

  return matrix;
};

// Assuming the data format returned by extractEtfHoldingsLinks

/**
 * Finds the top N tickers with the highest cumulative weight across all ETFs
 * @param links {Array<{from: string, to: string, value: number}>}
 * @param topN {number}
 * @returns {Array<{ticker: string, totalWeight: number}>}
 */
export function findTopOverlappedTickers(links, topN = 5) {
  const tickerWeights = links.reduce((acc, { from, value }) => {
    acc[from] = (acc[from] || 0) + value;
    return acc;
  }, {});

  return Object.entries(tickerWeights)
    .map(([ticker, totalWeight]) => ({ ticker, totalWeight }))
    .sort((a, b) => b.totalWeight - a.totalWeight)
    .slice(0, topN);
}



export const transformMatrixToHeatmapData = (matrix, keys) => {
  return matrix.map((row, rowIndex) => {
    let children = [];
    row.forEach((cell, cellIndex) => {
        children.push({
            x: keys[cellIndex],
            y: cell
        });
    });
    const rowData = { id: keys[rowIndex], data: children };
    return rowData;
  });
};

/**
 * Finds the ETF with the most unique holdings.
 * @param {Array<{from: string, to: string, value: number}>} links - Array of holding links between tickers and ETFs.
 * @returns {string} The ETF with the most unique holdings.
 */
export function findEtfWithMostUnique(links) {
  const etfHoldingsMap = new Map();

  links.forEach(({ from, to }) => {
    if (!etfHoldingsMap.has(to)) {
      etfHoldingsMap.set(to, new Set());
    }
    etfHoldingsMap.get(to).add(from);
  });

  let etfWithMostUnique = '';
  let maxUniqueCount = 0;

  etfHoldingsMap.forEach((holdings, etf) => {
    const uniqueHoldings = new Set([...holdings].filter(ticker =>
      [...etfHoldingsMap.entries()].every(([otherEtf, otherHoldings]) => etf === otherEtf || !otherHoldings.has(ticker))
    ));
    if (uniqueHoldings.size > maxUniqueCount) {
      etfWithMostUnique = etf;
      maxUniqueCount = uniqueHoldings.size;
    }
  });

  return {
    key: etfWithMostUnique,
    value: maxUniqueCount
  };
}

/**
 * Finds the ETF with the most connections to other ETFs.
 * @param {Array<{from: string, to: string, value: number}>} links - Array of holding links between tickers and ETFs.
 * @returns {string} The ETF with the most connections.
 */
export function findEtfWithMostConnections(links) {
  const connectionsMap = new Map();

  links.forEach(({ from, to }) => {
    if (!connectionsMap.has(from)) {
      connectionsMap.set(from, new Set());
    }
    connectionsMap.get(from).add(to);
  });

  let etfWithMostConnections = '';
  let maxConnections = 0;

  connectionsMap.forEach((etfs, ticker) => {
    etfs.forEach(etf => {
      const connectionCount = connectionsMap.get(ticker).size;
      if (connectionCount > maxConnections) {
        etfWithMostConnections = etf;
        maxConnections = connectionCount;
      }
    });
  });

  // This counts direct connections based on shared holdings
  // An alternative approach may involve a more complex calculation to consider indirect connections as well
  return {
    key: etfWithMostConnections,
    value: maxConnections
  };
}



